/**
 * How a plan's dates read when they are not being edited.
 *
 * A date picker is a control: it invites a click, carries a calendar affordance
 * and renders an empty one as "Set date" — which reads as an instruction on a
 * page nobody can currently edit. Outside edit mode these are facts, so they are
 * rendered as text, and an absent one says so rather than looking like a control
 * waiting to be used.
 */

/** The year is dropped when it is this one — "15 Sep" is how a school year is
 *  spoken, and repeating 2026 down thirty rows is noise. A date in another year
 *  keeps it, which is exactly when it matters. */
export function formatPlanDate(iso: string | undefined | null): string | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, sameYear
    ? { day: 'numeric', month: 'short' }
    : { day: 'numeric', month: 'short', year: 'numeric' });
}

export const NO_DATE = 'no date set';

/**
 * How far into a plan today already is — "5 weeks and 16% already in".
 *
 * A plan taken mid-session is not the plan its author wrote: a student adopting
 * in November inherits dates that passed in August. Said on the row, so the cost
 * is visible while choosing rather than after taking.
 *
 * Computed from the plan's OWN dates rather than a server verdict, so it works
 * for any plan the list can show — including one the browse endpoint never
 * scored. Null when there is nothing to say: no dates, or a start so recent
 * that remarking on it teaches the reader to ignore the warning that matters.
 */
const WARN_ABOVE_FRACTION = 0.1;

/** "4 months" reads better than "123 days" at every scale that matters here,
 *  and a plan is authored in months. */
function elapsedWords(days: number): string {
  if (days < 14) return `${days} day${days === 1 ? '' : 's'}`;
  if (days < 60) return `${Math.round(days / 7)} weeks`;
  return `${Math.round(days / 30)} months`;
}

export function planElapsed(plan: {
  passDates?: Record<string, string | undefined>;
  subjects?: Array<{ chapterTargets?: Record<string, Array<{ targetDate?: string }> | undefined> }>;
}): string | null {
  const dates = [
    ...Object.values(plan.passDates ?? {}),
    ...(plan.subjects ?? []).flatMap(s =>
      Object.values(s.chapterTargets ?? {}).flatMap(t => (t ?? []).map(x => x.targetDate))),
  ].filter((d): d is string => !!d).sort();
  if (dates.length === 0) return null;

  const day = 86_400_000;
  const at = (iso: string) => Date.parse(`${iso.slice(0, 10)}T00:00:00`);
  const start = at(dates[0]);
  const end = at(dates[dates.length - 1]);
  const now = at(new Date().toISOString());
  if (!Number.isFinite(start) || !Number.isFinite(end) || now <= start) return null;

  // Every date gone: the shape may still suit, but no fraction describes it.
  if (now >= end) return 'every date in this plan has already passed';

  const fraction = (now - start) / (end - start);
  if (fraction <= WARN_ABOVE_FRACTION) return null;
  return `${elapsedWords(Math.round((now - start) / day))} and ${Math.round(fraction * 100)}% already in`;
}
